import React, { useState, useEffect } from "react";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import {
  Card,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Divider,
} from "@material-ui/core";
import Button from "../button";
import { injectIntl } from "react-intl";
import Typography from "../typography";
import messages from "./PageViewMatch.intl";
import { CONFIGURE_MATCH } from "./MatchQueries";
import { useMutation } from "@apollo/client";
import Formation11 from "./Formation11";
import Formation7 from "./Formation7";
import Formation9 from "./Formation9";
import Formation5 from "./Formation5";
import SettingsIcon from "@material-ui/icons/Settings";
import PeopleIcon from "@material-ui/icons/People";
import TimerIcon from "@material-ui/icons/Timer";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import SportsIcon from "@material-ui/icons/Sports";

const CardWrapper = styled(Card)(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  borderRadius: 16,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  overflow: "hidden",
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    borderRadius: 12,
    marginBottom: theme.spacing(2),
  },
}));

const Header = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(2.5, 3),
  color: "#fff",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const HeaderIcon = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& svg": {
    fontSize: "2rem",
  },
}));

const Title = styled(Typography.H6)(() => ({
  color: "#fff",
  fontWeight: 700,
  margin: 0,
  fontSize: "1.25rem",
}));

const Content = styled("div")(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const ConfigSection = styled(Box)(({ theme }: IStyledArguments) => ({
  marginBottom: theme.spacing(3),
}));

const SectionTitle = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.main,
  fontWeight: 600,
  fontSize: "1rem",
  "& svg": {
    fontSize: "1.5rem",
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }: IStyledArguments) => ({
  width: "100%",
  marginBottom: theme.spacing(2),
  "& .MuiInputLabel-root": {
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: theme.palette.primary.main,
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    background: theme.palette.background.paper,
    transition: "border-color 0.2s ease",
    "&.Mui-focused": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },
  },
  "& .MuiSelect-select": {
    padding: theme.spacing(1.75, 2),
  },
}));

const StyledSwitch = styled(FormControlLabel)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.accentSoft,
  borderRadius: 12,
  padding: theme.spacing(1.5, 2),
  margin: 0,
  width: "100%",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  "& .MuiFormControlLabel-label": {
    fontWeight: 500,
    color: theme.palette.text.primary,
    flex: 1,
  },
}));

const SaveButton = styled(Button)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.success,
  color: "#fff",
  borderRadius: 12,
  padding: theme.spacing(1.5, 4),
  fontWeight: 600,
  fontSize: "1rem",
  textTransform: "none",
  transition: "background 0.2s ease",
  "&:hover": {
    background: theme.palette.extra.success,
    opacity: 0.9,
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    padding: theme.spacing(1.25, 3),
  },
}));

const ConfigSummaryCard = styled(Box)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.successSoft,
  borderRadius: 12,
  padding: theme.spacing(2.5),
  marginBottom: theme.spacing(3),
  border: `1px solid ${theme.palette.extra.success}`,
}));

const ConfigRow = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1, 0),
  borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
  "&:last-child": {
    borderBottom: "none",
  },
}));

const ConfigLabel = styled(Typography.Subtitle1)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  color: theme.palette.text.secondary,
  fontWeight: 500,
  fontSize: "0.95rem",
  "& svg": {
    fontSize: "1.2rem",
    color: theme.palette.primary.main,
  },
}));

const ConfigValue = styled(Typography.Subtitle1)(({ theme }: IStyledArguments) => ({
  color: theme.palette.text.primary,
  fontWeight: 700,
  fontSize: "1rem",
}));

const FormationSection = styled(Box)(({ theme }: IStyledArguments) => ({
  marginTop: theme.spacing(3),
}));

const FormationTitle = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.main,
  fontWeight: 700,
  fontSize: "1.1rem",
  "& svg": {
    fontSize: "1.75rem",
  },
}));

const numberOfPlayers = [5, 7, 9, 11];
const numberOfParts = [1, 2, 4];
const timeOfPart = [10, 15, 20, 25, 30, 35, 40, 45];
const numOfChanges = [3, 4, 5];

const MatchConfigurationCard = injectIntl<any>(
  ({ id, matchConfiguration, team, onChange, onConfigured, lineUp, intl }) => {
    console.log("[matchConfigurationCard] lineup -> ", lineUp);

    const [selectedNumberOfPlayers, setSelectedNumberOfPlayers] = useState<
      number
    >();
    const [selectedNumberOfParts, setSelectedNumberOfParts] = useState<
      number
    >();
    const [selectedTimeOfPart, setSelectedTimeOfPart] = useState<number>();
    const [selectedNumOfChanges, setSelectedNumOfChanges] = useState<number>();
    const [noLimitInSubs, setNoLimitInSubs] = useState<boolean>(false);
    const [configureMatch, { data }] = useMutation(CONFIGURE_MATCH);
    const [config, setConfig] = useState<any>(
      matchConfiguration && { ...matchConfiguration }
    );
    const [initialFormation, setInitialFormation] = useState<number>();

    useEffect(() => {
      if (lineUp) {
        setInitialFormation(lineUp);
      }
    }, [lineUp]);

    useEffect(() => {
      if (initialFormation) {
        console.log("[MatchConfigurationCard] initial formation changed...");
        onChange(initialFormation);
      }
    }, [initialFormation]);

    useEffect(() => {
      if (data && data.configureMatch) {
        setConfig({
          numPlayers: data.configureMatch.config.numPlayers,
          gameParts: data.configureMatch.config.gameParts,
          timePerPart: data.configureMatch.config.timePerPart,
          substitutions: data.configureMatch.config.substitutions,
        });
        onConfigured();
      }
      if (config) {
        console.log("config has changed!!", config);
        console.log(config);
      }
    }, [data]);

    const handleNumberOfPlayersChange = (e: any) => {
      console.log("handleNumberOfPlayersChange");
      console.log(e);
      setSelectedNumberOfPlayers(e);
    };

    const handleNumberOfPartsChange = (e: any) => {
      setSelectedNumberOfParts(e);
    };
    const handleTimeOfPartChange = (e: any) => {
      setSelectedTimeOfPart(e);
    };
    const handleNumOfChangesChange = (e: any) => {
      setSelectedNumOfChanges(e);
    };
    const handleSave = () => {
      console.log({
        numPlayers: selectedNumberOfPlayers,
        gameParts: selectedNumberOfParts,
        timePerPart: selectedTimeOfPart,
      });
      configureMatch({
        variables: {
          matchId: id,
          numPlayers: Number(selectedNumberOfPlayers),
          gameParts: Number(selectedNumberOfParts),
          timePerPart: Number(selectedTimeOfPart),
          substitutions: noLimitInSubs ? -1 : Number(selectedNumOfChanges),
        },
      });
    };
    const handleNoLimitInSubs = (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextState = e.target.checked;
      setNoLimitInSubs(nextState);
    };

    return (
      <CardWrapper>
        <Header>
          <HeaderIcon>
            <SettingsIcon />
          </HeaderIcon>
          <Title>{intl.formatMessage(messages.configureMatch)}</Title>
        </Header>
        <Content>
          {!config && (
            <>
              <ConfigSection>
                <SectionTitle>
                  <PeopleIcon />
                  <span>Players & Game Format</span>
                </SectionTitle>
                
                <StyledFormControl variant="outlined">
                  <InputLabel>Number of players</InputLabel>
                  <Select
                    value={selectedNumberOfPlayers || ""}
                    onChange={(e: any) => handleNumberOfPlayersChange(e.target.value)}
                    label="Number of players"
                  >
                    <MenuItem value="">
                      <em>Select...</em>
                    </MenuItem>
                    {numberOfPlayers.map((num, idx) => (
                      <MenuItem key={idx} value={num}>
                        {num} players
                      </MenuItem>
                    ))}
                  </Select>
                </StyledFormControl>

                <StyledFormControl variant="outlined">
                  <InputLabel>Number of parts</InputLabel>
                  <Select
                    value={selectedNumberOfParts || ""}
                    onChange={(e: any) => handleNumberOfPartsChange(e.target.value)}
                    label="Number of parts"
                  >
                    <MenuItem value="">
                      <em>Select...</em>
                    </MenuItem>
                    {numberOfParts.map((num, idx) => (
                      <MenuItem key={idx} value={num}>
                        {num} {num === 1 ? "part" : "parts"}
                      </MenuItem>
                    ))}
                  </Select>
                </StyledFormControl>
              </ConfigSection>

              <Divider style={{ marginBottom: "24px" }} />

              <ConfigSection>
                <SectionTitle>
                  <TimerIcon />
                  <span>Time Settings</span>
                </SectionTitle>

                <StyledFormControl variant="outlined">
                  <InputLabel>Time per part (minutes)</InputLabel>
                  <Select
                    value={selectedTimeOfPart || ""}
                    onChange={(e: any) => handleTimeOfPartChange(e.target.value)}
                    label="Time per part (minutes)"
                  >
                    <MenuItem value="">
                      <em>Select...</em>
                    </MenuItem>
                    {timeOfPart.map((time, idx) => (
                      <MenuItem key={idx} value={time}>
                        {time} minutes
                      </MenuItem>
                    ))}
                  </Select>
                </StyledFormControl>
              </ConfigSection>

              <Divider style={{ marginBottom: "24px" }} />

              <ConfigSection>
                <SectionTitle>
                  <SwapHorizIcon />
                  <span>Substitutions</span>
                </SectionTitle>

                <Box mb={2}>
                  <StyledSwitch
                    control={
                      <Switch
                        checked={noLimitInSubs}
                        onChange={handleNoLimitInSubs}
                        color="primary"
                      />
                    }
                    label={intl.formatMessage(messages.noLimitSubstitutions)}
                  />
                </Box>

                <StyledFormControl variant="outlined" disabled={noLimitInSubs}>
                  <InputLabel>Number of substitutions</InputLabel>
                  <Select
                    value={selectedNumOfChanges || ""}
                    onChange={(e: any) => handleNumOfChangesChange(e.target.value)}
                    label="Number of substitutions"
                  >
                    <MenuItem value="">
                      <em>Select...</em>
                    </MenuItem>
                    {numOfChanges.map((num, idx) => (
                      <MenuItem key={idx} value={num}>
                        {num} substitutions
                      </MenuItem>
                    ))}
                  </Select>
                </StyledFormControl>
              </ConfigSection>

              <Box mt={3}>
                <SaveButton
                  fullWidth
                  variant="contained"
                  onClick={handleSave}
                  startIcon={<CheckCircleIcon />}
                >
                  Save Configuration
                </SaveButton>
              </Box>
            </>
          )}
          {config && (
            <>
              <ConfigSummaryCard>
                <ConfigRow>
                  <ConfigLabel>
                    <PeopleIcon />
                    {intl.formatMessage(messages.numPlayers)}
                  </ConfigLabel>
                  <ConfigValue>{config.numPlayers} players</ConfigValue>
                </ConfigRow>
                <ConfigRow>
                  <ConfigLabel>
                    <TimerIcon />
                    {intl.formatMessage(messages.gameParts)}
                  </ConfigLabel>
                  <ConfigValue>
                    {config.gameParts} {config.gameParts === 1 ? "part" : "parts"}
                  </ConfigValue>
                </ConfigRow>
                <ConfigRow>
                  <ConfigLabel>
                    <TimerIcon />
                    {intl.formatMessage(messages.timePerPart)}
                  </ConfigLabel>
                  <ConfigValue>{config.timePerPart} minutes</ConfigValue>
                </ConfigRow>
                <ConfigRow>
                  <ConfigLabel>
                    <SwapHorizIcon />
                    {intl.formatMessage(messages.substitutions)}
                  </ConfigLabel>
                  <ConfigValue>
                    {config.substitutions === -1 ? "No limit" : `${config.substitutions} max`}
                  </ConfigValue>
                </ConfigRow>
              </ConfigSummaryCard>

              <Divider />

              <FormationSection>
                <FormationTitle>
                  <SportsIcon />
                  {intl.formatMessage(messages.selectFormation)}
                </FormationTitle>
                
                {config.numPlayers === 11 && (
                  <Formation11
                    matchId={id}
                    lineUp={initialFormation}
                    team={team}
                    callback={(lineUp: any) => {
                      console.log("Seleccionados::::", lineUp);
                      setInitialFormation(lineUp);
                    }}
                  />
                )}
                {config.numPlayers === 9 && (
                  <Formation9
                    matchId={id}
                    lineUp={initialFormation}
                    team={team}
                    callback={(lineUp: any) => {
                      console.log("Seleccionados::::", lineUp);
                      setInitialFormation(lineUp);
                    }}
                  />
                )}
                {config.numPlayers === 7 && (
                  <Formation7
                    matchId={id}
                    lineUp={initialFormation}
                    team={team}
                    callback={(lineUp: any) => {
                      console.log("Seleccionados::::", lineUp);
                      setInitialFormation(lineUp);
                    }}
                  />
                )}
                {config.numPlayers === 5 && (
                  <Formation5
                    matchId={id}
                    lineUp={initialFormation}
                    team={team}
                    callback={(lineUp: any) => {
                      console.log("Seleccionados::::", lineUp);
                      setInitialFormation(lineUp);
                    }}
                  />
                )}
              </FormationSection>
            </>
          )}
        </Content>
      </CardWrapper>
    );
  }
);

export default MatchConfigurationCard;
